import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { CompetitorData } from '@/experiences/sales-portal/domain/pricing/screens/quotes/blocs/hooks/use-quote-state-types';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import {
  PRICE_INPUT_REGEX,
  stripCommas,
} from '@/domain/pricing/utils/pricing-utils';
import { TextInput, View } from 'react-native';
import { useReportCellFocus } from '@experiences/components/table/blocs/hooks/use-report-cell-focus';

type CompetitorField = 'itemName' | 'packSize' | 'price';

interface CompetitorFieldsCellProps {
  competitor?: CompetitorData;
  currencyCode: string;
  field: CompetitorField;
  localId: string;
  onUpdateCompetitor: (localId: string, competitor: CompetitorData) => void;
  rowIndex: number;
}

const FIELD_PLACEHOLDERS: Record<CompetitorField, string | undefined> = {
  itemName: undefined,
  packSize: undefined,
  price: '0.00',
};

const FIELD_LABEL_KEYS: Record<CompetitorField, string> = {
  itemName: 'sales_portal.price_quotes.table.item_name',
  packSize: 'sales_portal.price_quotes.table.pack_size',
  price: 'sales_portal.price_quotes.table.price',
};

/**
 * Inline-editable competitor cell for `itemName`, `packSize`, or `price`.
 * Mirrors EditablePriceCell / EditableQuantityCell: shows committed value
 * while idle, raw value while focused, commits on blur. The price variant
 * adds an input gate (digits + commas + one decimal), strips commas before
 * commit, and renders formatted currency while idle. Renders nothing when
 * the line has no competitor stub yet (i.e. before SET_COMPETITOR_NAME has
 * seeded the competitor row).
 */
export const CompetitorFieldsCell: React.FC<CompetitorFieldsCellProps> = ({
  competitor,
  currencyCode,
  field,
  localId,
  onUpdateCompetitor,
  rowIndex,
}) => {
  const [styles, theme] = useStyles();
  const currentValue = competitor ? (competitor[field] ?? '') : '';
  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState(currentValue);
  const reportCellFocus = useReportCellFocus(rowIndex);

  if (!competitor) {
    return null;
  }

  const isPriceField = field === 'price';

  const handleFocus = () => {
    reportCellFocus();
    setValue(currentValue);
    setIsFocused(true);
  };

  const handleChangeText = (text: string) => {
    if (isPriceField) {
      if (text && !PRICE_INPUT_REGEX.test(text)) {
        return;
      }
      setValue(stripCommas(text));
    } else {
      setValue(text);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (isPriceField) {
      const parsed = parseFloat(value);
      // Reject zero / non-numeric — matches the our-price cell's rule.
      if (isNaN(parsed) || parsed <= 0) {
        setValue(currentValue);
        return;
      }
    }
    // Skip commit on a no-op blur so just tabbing through doesn't mark
    // the quote dirty.
    if (value === currentValue) {
      return;
    }
    onUpdateCompetitor(localId, { ...competitor, [field]: value });
  };

  const idleDisplay =
    isPriceField && currentValue
      ? formatCurrency(
          parseFloat(currentValue) * MICRO_CONVERSION,
          currencyCode,
        )
      : currentValue;

  return (
    <View style={styles.container}>
      <TextInput
        accessibilityLabel={i18n.t(FIELD_LABEL_KEYS[field])}
        keyboardType={isPriceField ? 'decimal-pad' : 'default'}
        onBlur={handleBlur}
        onChangeText={handleChangeText}
        onFocus={handleFocus}
        placeholder={FIELD_PLACEHOLDERS[field]}
        placeholderTextColor={theme.sk.color.text.tertiary}
        selectTextOnFocus={isPriceField}
        style={[styles.input, isFocused && styles.inputFocused]}
        value={isFocused ? value : idleDisplay}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-start',
    flex: 1,
    justifyContent: 'center',
  },
  input: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    color: theme.sk.color.text.primary,
    fontFamily: theme.sk.typography.paragraph.md.fontFamily,
    fontSize: theme.sk.typography.paragraph.md.fontSize,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceSmall,
    textAlign: 'left',
    width: '100%',
  },
  inputFocused: {
    borderColor: theme.sk.color.border.input.active,
  },
}));
